import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  MapPin,
  BedDouble,
  Maximize2,
  Banknote,
  ExternalLink,
  Trash2,
  Calendar,
  Tag,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  LeafIcon,
} from "lucide-react";
import type { Listing, ListingImage } from "../types";

interface ListingDetailProps {
  listing: Listing;
  onClose: () => void;
  onDelete: () => void;
}

export function ListingDetail({
  listing,
  onClose,
  onDelete,
}: ListingDetailProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const images = listing.images.filter((img) => img.localFilename);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (lightboxIndex !== null) {
          setLightboxIndex(null);
        } else {
          onClose();
        }
      }
      if (lightboxIndex !== null) {
        if (e.key === "ArrowLeft")
          setLightboxIndex((p) => Math.max(0, (p ?? 0) - 1));
        if (e.key === "ArrowRight")
          setLightboxIndex((p) => Math.min(images.length - 1, (p ?? 0) + 1));
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, lightboxIndex, images.length]);

  return (
    <div className="animate-in fade-in duration-300 flex flex-col min-h-0 flex-1">
      {/* Compact listing info bar */}
      <div className="sticky top-16 z-30 bg-card/90 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6">
          {/* Top row: back + address + actions */}
          <div className="flex items-center gap-4 h-14">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors shrink-0 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Tillbaka</span>
            </button>

            <div className="h-5 w-px bg-border/50" />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                {listing.housingForm && (
                  <span className="shrink-0 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-semibold tracking-wider uppercase">
                    {listing.housingForm}
                  </span>
                )}
                <h2 className="font-display text-base font-semibold text-foreground truncate">
                  {listing.streetAddress ?? "Okänd adress"}
                </h2>
                {listing.askingPriceFormatted && (
                  <span className="shrink-0 text-primary font-semibold text-sm hidden sm:inline">
                    {listing.askingPriceFormatted}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Quick stats - desktop only */}
              <div className="hidden lg:flex items-center gap-3 mr-2">
                {listing.energyClass && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <LeafIcon className="w-3.5 h-3.5" />
                    <span>{listing.energyClass}</span>
                  </div>
                )}
                {listing.numberOfRooms != null && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <BedDouble className="w-3.5 h-3.5" />
                    <span>{listing.numberOfRooms} rum</span>
                  </div>
                )}
                {listing.livingArea && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>{listing.livingArea}</span>
                  </div>
                )}
                {listing.fee && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Banknote className="w-3.5 h-3.5" />
                    <span>{listing.fee}/mån</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => setInfoExpanded(!infoExpanded)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all"
              >
                {infoExpanded ? "Dölj info" : "Mer info"}
              </button>

              {listing.hemnetUrl && (
                <a
                  href={listing.hemnetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg hover:bg-secondary/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
                  title="Visa på Hemnet"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button
                onClick={onDelete}
                className="w-8 h-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive transition-all"
                title="Ta bort"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Expandable detail panel */}
          {infoExpanded && (
            <div className="pb-4 pt-1 border-t border-border/30 animate-in slide-in-from-top-2 fade-in duration-200">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-6 pt-3">
                {/* Location & price */}
                <div className="space-y-2">
                  {listing.askingPriceFormatted && (
                    <div className="text-primary font-semibold text-xl tracking-tight">
                      {listing.askingPriceFormatted}
                      {listing.squareMeterPrice != null && (
                        <span className="text-xs font-normal text-muted-foreground ml-2">
                          (
                          {Math.round(listing.squareMeterPrice).toLocaleString(
                            "sv-SE",
                          )}{" "}
                          kr/m²)
                        </span>
                      )}
                    </div>
                  )}
                  {(listing.locationFullName || listing.postCode) &&
                    (() => {
                      const locationText = [
                        listing.streetAddress,
                        listing.locationFullName,
                        listing.postCode,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}`;
                      return (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/map flex items-center gap-1.5 text-muted-foreground text-sm hover:text-primary transition-colors w-fit"
                        >
                          <MapPin className="w-3.5 h-3.5" />
                          <span className="group-hover/map:underline">
                            {[
                              listing.streetAddress,
                              listing.locationName,
                              listing.postCode,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover/map:opacity-100 transition-opacity" />
                        </a>
                      );
                    })()}
                  {listing.daysOnHemnet != null && (
                    <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                      <Calendar className="w-3 h-3" />
                      <span>{listing.daysOnHemnet} dagar på Hemnet</span>
                    </div>
                  )}
                </div>

                {/* Details table */}
                <DetailMini listing={listing} />

                {/* Tags + description */}
                <div className="space-y-3">
                  {listing.description && (
                    <button
                      onClick={() => setDescriptionOpen(true)}
                      className="w-full text-left group cursor-pointer"
                    >
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">
                        {listing.description}
                      </p>
                      <span className="text-[11px] text-primary group-hover:text-primary/80 font-medium mt-1.5 inline-block transition-colors">
                        Läs hela beskrivningen
                      </span>
                    </button>
                  )}
                  <FeatureTags listing={listing} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Moodboard: Pinterest masonry grid */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-6 py-6">
        {images.length > 0 ? (
          <MasonryGrid
            images={images}
            listingAddress={listing.streetAddress}
            onImageClick={(i) => setLightboxIndex(i)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/30 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-muted-foreground/40" />
            </div>
            <p className="text-muted-foreground text-sm">
              Inga bilder tillgängliga
            </p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onChangeIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          listingAddress={listing.streetAddress}
        />
      )}

      {/* Description modal */}
      {descriptionOpen && listing.description && (
        <DescriptionModal
          text={listing.description}
          onClose={() => setDescriptionOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── Row-order masonry grid ─── */
function useColumnCount() {
  const [cols, setCols] = useState(3);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setCols(w < 640 ? 2 : w < 1024 ? 3 : 4);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return cols;
}

function MasonryGrid({
  images,
  listingAddress,
  onImageClick,
}: {
  images: ListingImage[];
  listingAddress: string | null;
  onImageClick: (index: number) => void;
}) {
  const colCount = useColumnCount();

  // Distribute images round-robin into columns for row-order loading
  const columns = useMemo(() => {
    const cols: { image: ListingImage; originalIndex: number }[][] = Array.from(
      { length: colCount },
      () => [],
    );
    images.forEach((img, i) => {
      cols[i % colCount]!.push({ image: img, originalIndex: i });
    });
    return cols;
  }, [images, colCount]);

  return (
    <div className="flex gap-3" style={{ alignItems: "flex-start" }}>
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-3">
          {col.map(({ image, originalIndex }) => (
            <MoodboardImage
              key={image.id}
              image={image}
              index={originalIndex}
              alt={`${listingAddress ?? "Bostad"} - bild ${originalIndex + 1}`}
              onClick={() => onImageClick(originalIndex)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── Masonry image tile ─── */
function MoodboardImage({
  image,
  index,
  alt,
  onClick,
}: {
  image: ListingImage;
  index: number;
  alt: string;
  onClick: () => void;
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="group cursor-pointer animate-in fade-in slide-in-from-bottom-2"
      style={{
        animationDelay: `${Math.min(index * 40, 600)}ms`,
        animationFillMode: "backwards",
      }}
      onClick={onClick}
    >
      <div className="relative rounded-xl overflow-hidden bg-secondary/30 border border-border/20 hover:border-primary/30 transition-all duration-200 hover:shadow-lg hover:shadow-primary/5">
        <img
          src={`/api/images/${encodeURI(image.localFilename ?? "")}`}
          alt={alt}
          className={`w-full h-auto block transition-all duration-500 group-hover:scale-[1.02] ${loaded ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
        {!loaded && (
          <div className="absolute inset-0 bg-secondary/50 animate-pulse rounded-xl aspect-4/3" />
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <ZoomIn className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Lightbox overlay ─── */
function Lightbox({
  images,
  currentIndex,
  onChangeIndex,
  onClose,
  listingAddress,
}: {
  images: ListingImage[];
  currentIndex: number;
  onChangeIndex: (i: number) => void;
  onClose: () => void;
  listingAddress: string | null;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center animate-in fade-in duration-200">
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition-all"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-md text-xs text-white/70 tabular-nums">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center w-full px-16 py-4">
        <img
          src={`/api/images/${encodeURI(images[currentIndex]?.localFilename ?? "")}`}
          alt={listingAddress ?? ""}
          className="max-w-full max-h-full object-contain rounded-lg animate-in fade-in zoom-in-95 duration-200"
          key={currentIndex}
        />
      </div>

      {/* Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={() => onChangeIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition-all disabled:opacity-20 disabled:cursor-default"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() =>
              onChangeIndex(Math.min(images.length - 1, currentIndex + 1))
            }
            disabled={currentIndex === images.length - 1}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition-all disabled:opacity-20 disabled:cursor-default"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div
          className="flex gap-2 px-4 pb-4 overflow-x-auto max-w-full"
          style={{ scrollbarWidth: "none" }}
        >
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => onChangeIndex(i)}
              className={`shrink-0 w-16 h-11 rounded-lg overflow-hidden border-2 transition-all ${
                i === currentIndex
                  ? "border-primary opacity-100 scale-105"
                  : "border-transparent opacity-40 hover:opacity-70"
              }`}
            >
              <img
                src={`/api/images/${encodeURI(img.localFilename ?? "")}`}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Description modal ─── */
function DescriptionModal({
  text,
  onClose,
}: {
  text: string;
  onClose: () => void;
}) {
  const paragraphs = text.split(/\n+/).filter(Boolean);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative bg-card border border-border/40 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] mx-4 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/30">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-primary/60" />
            <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Beskrivning
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-secondary/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto max-h-[calc(80vh-4rem)]">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Mini detail table ─── */
function DetailMini({ listing }: { listing: Listing }) {
  const rows = [
    { label: "Bostadstyp", value: listing.housingForm },
    { label: "Upplåtelseform", value: listing.tenure },
    {
      label: "Antal rum",
      value:
        listing.numberOfRooms != null ? `${listing.numberOfRooms} rum` : null,
    },
    { label: "Boarea", value: listing.livingArea },
    { label: "Biarea", value: listing.supplementalArea },
    { label: "Tomtarea", value: listing.plotArea },
    { label: "Byggår", value: listing.constructionYear },
    { label: "Avgift", value: listing.fee ? `${listing.fee}/mån` : null },
    { label: "Driftkostnad", value: listing.runningCosts },
    { label: "Energiklass", value: listing.energyClass },
  ].filter((r): r is { label: string; value: string } => r.value != null);

  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-baseline justify-between gap-2 text-xs py-0.5"
        >
          <span className="text-muted-foreground">{row.label}</span>
          <span className="font-medium text-foreground">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Feature tags ─── */
function FeatureTags({ listing }: { listing: Listing }) {
  const amenities: string[] = listing.amenities
    ? JSON.parse(listing.amenities)
    : [];
  const labelItems: { text: string; category: string }[] = listing.labels
    ? JSON.parse(listing.labels)
    : [];
  const featureTags = [
    ...amenities,
    ...labelItems.filter((l) => l.category === "FEATURE").map((l) => l.text),
  ];
  const uniqueTags = [...new Set(featureTags)];
  if (uniqueTags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {uniqueTags.slice(0, 8).map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary/60 border border-border/30 text-[10px] text-foreground"
        >
          <Tag className="w-2.5 h-2.5 text-muted-foreground" />
          {tag}
        </span>
      ))}
      {uniqueTags.length > 8 && (
        <span className="text-[10px] text-muted-foreground self-center">
          +{uniqueTags.length - 8} till
        </span>
      )}
    </div>
  );
}
